import type { ObjectId } from '@freightview/object-id'

import type { AIModel } from '../admin/ai.ts'
import type { DocumentLoadMatchMethod } from '../admin/document-ai.ts'
import {
  vArray,
  vLoadRef,
  vObject,
  type vOutput,
  vPicklist,
  vString,
} from '../validation/index.ts'
import { loadDocTypes } from './load-document.ts'

export type AccountDocumentCenter = {
  identifier: string
  lastUpdated: Date
  identifierPrompt?: string
  refNumPrompt?: string
  listBillRefNumPrompt?: string
  singleInvoicePrompt?: string
  listInvoicePrompt?: string
  aiModel?: AIModel
}

export type AccountDocumentCenterDTO = Omit<
  AccountDocumentCenter,
  'identifier' | 'lastUpdated'
> & {
  email: string
}

export type DocumentEntrySource = 'email' | 'app'
export const docEntryStatuses = ['processing', 'processed'] as const
export type DocEntryStatus = (typeof docEntryStatuses)[number]
export interface DocumentEntry {
  _id: ObjectId
  accountId: ObjectId
  userId: ObjectId
  batchId: ObjectId
  source: DocumentEntrySource
  document: DocEntryFileDetails
  status: DocEntryStatus
  loads?: DocEntryLoad[]
  createdDate: Date
  archivedDate?: Date
}
export interface DocEntryFileDetails {
  fileName: string
  mimeType?: string
  url: string
  docType?: ParseableDocType
  fileSize?: number
  pageCount?: number
  hasMultipleShipments?: boolean
}

export interface DocEntryLoad {
  loadId: ObjectId
  status: DocEntryLoadStatus
  reviewedDate?: Date
  matchDetails?: {
    method: DocumentLoadMatchMethod
    value: string
  }
}
const batchDocLoadStatus = ['pending', 'approved', 'rejected'] as const
export type DocEntryLoadStatus = (typeof batchDocLoadStatus)[number]

export const parseableDocTypes = [...loadDocTypes, 'list-bill'] as const
export type ParseableDocType = (typeof parseableDocTypes)[number]
interface ParsedLocation {
  company: string
  address: string
  city: string
  state: string
  postalCode: string
}
export type ParsedDocData = {
  pickupDate: string
  deliveryDate: string
  origin: ParsedLocation
  destination: ParsedLocation
  bolNumber: string
  trackingNumber: string
  poNumber: string
  shipperNumber: string
}
export type DocRefNumResult = {
  entries: ParsedDocData[]
}

export type DocIdentifierResult = {
  documentType: ParseableDocType
  carrier?: {
    code: string
    name: string
    website: string
  }
  containsMultipleShipments: boolean
  fileSize?: number
  pageCount?: number
}
export type DocProcessorResult = {
  identifierResult: DocIdentifierResult
  refNumResult: DocRefNumResult
}

export type DocCenterListItemIdentifier = `${string}-${string}`
export interface DocCenterListItem {
  entryId: ObjectId
  identifier: DocCenterListItemIdentifier
  source: DocumentEntrySource
  url: string
  status: DocEntryLoadStatus | DocEntryStatus | 'error'
  fileName: string
  mimeType?: string
  reviewedDate?: Date
  loadId: ObjectId
  matchDetails?: {
    method: DocumentLoadMatchMethod
    value: string
  }
  createdDate: Date
  batchId: ObjectId
  documentType: ParseableDocType
}

const bulkDocEntryReviewEntrySchema = vObject({
  identifier: vString(),
  docType: vPicklist(loadDocTypes),
})
export const bulkDocEntryReviewSchema = vObject({
  entries: vArray(bulkDocEntryReviewEntrySchema),
  action: vPicklist(['approve', 'reject']),
})
export type BulkDocEntryReviewRequest = vOutput<typeof bulkDocEntryReviewSchema>

export const associateDocLoadSchema = vObject({
  loadId: vLoadRef(),
  docType: vPicklist(loadDocTypes),
})
export type AssociateDocLoadRequest = vOutput<typeof associateDocLoadSchema>

export type BulkDocEntryReviewResponse = Array<{
  entryId: string
  loadId: string
  success: boolean
}>
